import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { Resolve, ActivatedRouteSnapshot } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { InterviewService } from '../services/interview.service';
import { MetaService } from '../../../shared/services/meta.service';
import { WordPressPost } from '../../../DTO/wordpress/post.dto';
import { environment } from '../../../../environments/environment';
import { isPlatformBrowser } from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class InterviewDetailsResolver implements Resolve<WordPressPost | null> {

  constructor(
    private interviewService: InterviewService,
    private metaService: MetaService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {}

  resolve(route: ActivatedRouteSnapshot): Observable<WordPressPost | null> {
    const slug = route.paramMap.get('slug')!;

    if (!slug) {
      return of(null);
    }

    return this.interviewService.getPostBySlug(slug).pipe(
      tap((post) => {
        if (post) {
          // Set meta tags immediately when data is available
          this.setMetaTags(post);
        }
      }),
      catchError((error) => {
        console.error('Error resolving interview details:', error);
        return of(null);
      })
    );
  }

  private setMetaTags(post: WordPressPost): void {
    const title = this.stripHtml(post.title.rendered);
    const description = this.getPostExcerpt(post, 110);
    const image = this.getPostFeaturedImage(post);
    const baseUrl = environment.frontendUrl || (isPlatformBrowser(this.platformId) ? window.location.origin : 'https://superlawyer.in');
    const url = `${baseUrl}/${post.slug}`; // Use dynamic frontend URL

    this.metaService.setSocialMetaTags({
      title,
      description,
      image,
      url,
      type: 'article',
      siteName: 'SuperLawyer',
      author: 'SuperLawyer Team'
    });
  }

  private stripHtml(html: string): string {
    return this.cleanHtml(html);
  }

  private getPostExcerpt(post: WordPressPost, wordLimit = 110): string {
    const source = post.magazineBlocksPostExcerpt
      || post.excerpt?.rendered
      || post.content?.rendered
      || '';

    const cleanText = this.cleanHtml(source);
    if (wordLimit <= 0) {
      return cleanText;
    }

    const words = cleanText.split(/\s+/).filter(Boolean);
    return words.length > wordLimit
      ? `${words.slice(0, wordLimit).join(' ')}...`
      : cleanText;
  }

  private cleanHtml(html: string): string {
    if (!html) {
      return '';
    }

    let text = html.replace(/<[^>]*>/g, '');

    text = text
      .replace(/&amp;#8220;/g, '"')
      .replace(/&amp;#8221;/g, '"')
      .replace(/&amp;#8216;/g, "'")
      .replace(/&amp;#8217;/g, "'")
      .replace(/&amp;#8211;/g, '-')
      .replace(/&amp;#8212;/g, '--')
      .replace(/&amp;#8230;/g, '...')
      .replace(/&amp;hellip;/g, '...')
      .replace(/&amp;#038;/g, '&')
      .replace(/&amp;/g, '&');

    text = text
      .replace(/&#8220;/g, '"')
      .replace(/&#8221;/g, '"')
      .replace(/&#8216;/g, "'")
      .replace(/&#8217;/g, "'")
      .replace(/&#8211;/g, '-')
      .replace(/&#8212;/g, '--')
      .replace(/&#8230;/g, '...')
      .replace(/&hellip;/g, '...')
      .replace(/&#038;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&apos;/g, "'")
      .replace(/&nbsp;/g, ' ');

    text = text.replace(/\u00A0/g, ' ').replace(/\u2026/g, '...');

    return text.replace(/\s+/g, ' ').trim();
  }

  private getPostFeaturedImage(post: WordPressPost): string {
    // First, try WordPress embedded featured media (standard WordPress way)
    if (post._embedded?.['wp:featuredmedia']?.[0]?.source_url) {
      const imageUrl = post._embedded['wp:featuredmedia'][0].source_url;
      return imageUrl;
    }

    // Second, try Magazine Blocks featured media
    if (post.magazineBlocksPostFeaturedMedia?.large) {
      return post.magazineBlocksPostFeaturedMedia.large;
    }

    // Third, try Magazine Blocks featured image URL
    if (post.magazine_blocks_featured_image_url?.full?.[0]) {
      return post.magazine_blocks_featured_image_url.full[0];
    }

    // Fallback to service method
    const fallbackImage = this.interviewService.getPostFeaturedImage(post);
    return fallbackImage;
  }
}
